/**
 *  Takes a courseId, firmCode, courseName and report object and returns a CSV string formatted as a table
 *  Example of report object
 *  {"csvResults":"Screen ID,Text segment,Explanation,Suggested correction,Severity\nmoderate_and_vigorous_exercise_adpteba,\"brisk walking until you can't talk without being out of breath\",\"The contraction 'can't' should be avoided in formal writing.\",\"brisk walking until you cannot talk without being out of breath\",Medium\nbeating_your_blockers_adpt4d3,\"being 'time poor' is a common reason for not exercising.\",\"The use of single quotation marks is inconsistent with the rest of the document, which uses double quotation marks.\",\"being \"\"time poor\"\" is a common reason for not exercising.\",Low","totalTokensUsed":3446}
 * @param courseId
 * @param firmCode
 * @param courseName
 * @param report
 *
 * @returns CSV string
 */

interface StyleGuideReport {
  csvResults: string;
  totalTokensUsed?: number;
}

/**
 * Cleans problematic Unicode characters that don't display well in Excel
 * @param text - The text to clean
 * @returns Cleaned text with ASCII equivalents
 */
function cleanUnicodeCharacters(text: string): string {
  return (
    text
      // Smart quotes
      .replace(/[\u2018\u2019]/g, "'") // Left/right single quotation marks → apostrophe
      .replace(/[\u201C\u201D]/g, '"') // Left/right double quotation marks → straight quotes
      .replace(/[\u201E\u201F]/g, '"') // Double low-9/high-reversed-9 quotation marks → straight quotes
      .replace(/[\u2039\u203A]/g, "'") // Single left/right-pointing angle quotation marks → apostrophe
      .replace(/[\u00AB\u00BB]/g, '"') // Left/right-pointing double angle quotation marks → straight quotes

      // Dashes and hyphens
      .replace(/[\u2013\u2014]/g, '-') // En dash, em dash → hyphen-minus
      .replace(/[\u2010\u2011]/g, '-') // Hyphen, non-breaking hyphen → hyphen-minus

      // Ellipsis
      .replace(/\u2026/g, '...') // Horizontal ellipsis → three dots

      // Spaces (but preserve line breaks)
      .replace(/[\u00A0\u2000-\u200B]/g, ' ') // Various Unicode spaces → regular space (excluding line separators)
      .replace(/[\u2028\u2029]/g, '\n') // Line separator, paragraph separator → newline

      // Other common problematic characters
      .replace(/\u2022/g, '*') // Bullet → asterisk
      .replace(/[\u2032\u2033]/g, "'") // Prime, double prime → apostrophe
      .replace(/\u00B0/g, ' degrees') // Degree symbol → text
      .replace(/[\u00D7]/g, 'x') // Multiplication sign → x
      .replace(/[\u00F7]/g, '/') // Division sign → forward slash

      // Remove control characters but preserve newlines (\n) and carriage returns (\r)
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, '')
  );
}

/**
 * Downloads a CSV string as a file in the browser
 * @param csvContent - The CSV content to download
 * @param filename - The filename for the download (optional, will generate if not provided)
 */
export function downloadCSV(csvContent: string, filename?: string): void {
  // Generate filename if not provided
  if (!filename) {
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    filename = `style-guide-report-${timestamp}.csv`;
  }

  // Ensure filename has .csv extension
  if (!filename.endsWith('.csv')) {
    filename += '.csv';
  }

  // Create blob and download
  const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const downloadUrl = window.URL.createObjectURL(csvBlob);

  // Create temporary link element
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.setAttribute('download', filename);
  link.classList.add('sr-only'); // Hide the link

  // Append to DOM, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  window.URL.revokeObjectURL(downloadUrl);
}

export default async function createResportCSV(
  courseId: number,
  firmCode: string,
  courseName: string,
  reportType: string,
  report: StyleGuideReport
): Promise<string> {
  // reportType can be 'styleGuide'

  if (reportType !== 'styleGuide') {
    throw new Error(`Unsupported report type: ${reportType}`);
  }

  if (!report.csvResults) {
    throw new Error('No CSV results found in report');
  }

  // Extract the CSV data
  const csvData = report.csvResults;

  // Create metadata header
  const timestamp = new Date().toLocaleString().replace(',', ' - ');
  const courseIdentifier = firmCode ? `${firmCode}_${courseId}` : `${courseId}`;

  let csvOutput = '';

  // Add enhanced metadata headers with clear course identification
  csvOutput += `Style Guide Report\n`;
  csvOutput += `Generated: ${timestamp}\n`;
  csvOutput += `\n`;
  csvOutput += `COURSE INFORMATION\n`;
  csvOutput += `Course Name: ${courseName}\n`;
  if (firmCode) {
    csvOutput += `Firm Code: ${firmCode}\n`;
    csvOutput += `Full Course Identifier: ${courseIdentifier}\n`;
  }
  if (report.totalTokensUsed) {
    csvOutput += `Tokens Used: ${report.totalTokensUsed}\n`;
  }
  csvOutput += `\n`; // Empty line separator
  csvOutput += `STYLE GUIDE FINDINGS\n`;

  // Clean up problematic Unicode characters for Excel compatibility
  const cleanedCsvData = cleanUnicodeCharacters(csvData);

  // Use the CSV data as-is since course ID is now included as first column
  csvOutput += cleanedCsvData;

  return csvOutput;
}
